// src/app/argent/enregistrements.js — l'enregistrement de la configuration, de
// la compta et des données de facturation (gardes, jeton de version, alerte
// d'écriture).
// CHEMIN ARGENT. Déplacé octet pour octet depuis src/App.jsx (P-3.5, squelette,
// partie 2) : seuls changent l'emballage (signature, retour) et les imports.

import { captureGarde } from '../../lib/sentry'
import { comptaApi } from '../../lib/compta-api'
import { saveCfgRemote } from '../../lib/cfg-api'
import { debutGeste, ecrireDonnees } from '../../lib/data-api'
import { evaluateSaveDataGuards } from '../../lib/save-data-guards'

export function enregistrements({
  setCfg, setLecturesIllisibles, data, setData, setGardeAlert, setCompta, currentUser,
}) {
  // Lot B : cfg persistée via /api/cfg (table cfg_config + miroir KV dual-write,
  // claudeKey expurgé serveur). setCfg APRÈS confirmation serveur ; erreur propagée
  // pour remontée en toast (LogementsPanel/ParamsPanel).
  // ⚠️ `saveCfgRemote` renvoie { cfg, avertissement } — PAS le blob directement.
  // Poser l'enveloppe dans `setCfg` remplacerait toute la configuration admin par
  // un objet à deux clés : l'application entière tomberait. Le blob persisté est
  // dans `.cfg`, et rien d'autre ne doit atterrir dans l'état.
  //
  // L'avertissement est RENVOYÉ à l'appelant plutôt qu'affiché ici : c'est un
  // enregistrement RÉUSSI dont la copie lue par les agents de ménage est restée
  // périmée. Le panneau qui a déclenché la sauvegarde est le seul à savoir où le
  // montrer — App.jsx n'a pas de canal d'affichage.
  const saveCfg = async c => {
    const { cfg: persisted, avertissement } = await saveCfgRemote(c)
    setCfg(persisted)
    return avertissement
  }
  // saveCompta hissé dans App.jsx (audit-finding F1) — avant, ComptaTab tenait
  // son propre state compta + écriture directe storage. App.jsx tient AUSSI un
  // state compta utilisé par calcChargesFixes pour la KPI "Marge nette FBZ"
  // dans Pilotage > Données. Les 2 ne se synchronisaient jamais → KPI affichée
  // fausse jusqu'au reload.
  // Lot 2 : compta persistée via /api/compta (table compta_config + miroir KV
  // dual-write côté serveur). setCompta APRÈS confirmation serveur (le blob
  // persisté fait foi). L'erreur est propagée pour remontée en toast (ComptaTab).
  const saveCompta = async c => { const persisted = await comptaApi.saveCompta(c); setCompta(persisted) }
  // `opts` ne sert qu'aux IMPORTS (F13) : `jeton` = celui des données dont
  // l'import part ; `surConflit` = ce qu'il faut faire d'un 409 à la place de
  // l'alerte d'écriture (l'import s'arrête et montre le bandeau). Les autres
  // appelants n'en passent pas : comportement inchangé.
  const saveData = async (d, opts = {}) => {
    // F13 (tour 4, R3) : l'issue du geste précédent ne vaut plus — une garde
    // côté écran (refus certain, rien d'envoyé) ne doit pas laisser lire un
    // vieux « incertain ».
    debutGeste()
    // PR #146 BUG 2 — defense-in-depth ADR-077 : aucun rôle non-admin ne
    // doit jamais écrire fbz8-data. Skip silencieux + console.warn avec
    // stack trace pour identifier le call site exact.
    //
    // ⚠ Cette garde NE COUVRE PAS le scénario admin légitime observé le
    // 2026-05-09 (banner garde déclenché côté admin). La cause racine de ce
    // scénario était `refreshAllIcal` avec fallback `: {}` quand
    // `storage.get` retourne null (cf. App.jsx:365 + rapport pessimist deep
    // docs/handoff/investigation-bug2-garde-cote-admin-2026-05-09.md). Le
    // fix root cause est dans refreshAllIcal — ce check role reste utile
    // pour les autres call sites de saveData côté ménage (incidents.js,
    // stock.js, etc. en cas de regression).
    if (currentUser?.role && currentUser.role !== 'admin') {
      console.warn(`[saveData] skip — role=${currentUser.role} (defense-in-depth ADR-077)`, new Error('saveData-blocked-non-admin').stack)
      return false
    }

    // Évaluation centralisée des 3 gardes (cf. src/lib/save-data-guards.js).
    // Logique inchangée vs. avant le refactor PR #146 BUG 2 — voir tests
    // Jest src/lib/save-data-guards.test.js (26 tests non-régression).
    const verdict = evaluateSaveDataGuards(data, d)
    if (verdict.blocked) {
      const message =
        verdict.type === 'global_50pct'
          ? 'GARDE GLOBALE DÉCLENCHÉE — écriture refusée'
          : verdict.type === 'per_mois'
            ? `GARDE PER-MOIS DÉCLENCHÉE — mois ${verdict.details.mk} vidé`
            : 'GARDE SUM DÉCLENCHÉE — montants chuteraient sans changement count'
      const enrichedDetails = { ...verdict.details, userRole: currentUser?.role || 'unknown' }
      console.error(`[saveData] ${message}`, enrichedDetails)
      captureGarde('saveData', message, enrichedDetails)
      setGardeAlert(enrichedDetails)
      return false
    }

    // F13 — l'écran reçoit `d` dans `ecrireDonnees` : AVANT l'écriture pour un
    // geste de Dimitri (comportement inchangé, sa saisie reste à l'écran même
    // refusée), APRÈS la réussite seulement pour un import (sinon un import
    // refusé remplaçait à l'écran une saisie de Dimitri déjà enregistrée).
    // ═══════════════════════════════════════════════════════════════════════
    // 🔴 L'ÉCHEC D'ÉCRITURE ÉTAIT AVALÉ — corrigé le 2026-08-05
    // ═══════════════════════════════════════════════════════════════════════
    // L'état écran était déjà mis à jour (setData) et l'erreur de persistance
    // partait dans la console : à l'écran, la saisie paraissait enregistrée,
    // alors que RIEN n'était parti en base. Au rechargement suivant, le travail
    // avait disparu — sans le moindre signal. Sur le chemin argent (loyers et
    // charges de sous-location), c'est une perte comptable silencieuse.
    //
    // `saveData` renvoie désormais true/false. Les appelants qui l'ATTENDENT
    // peuvent prévenir l'utilisateur ; ceux qui ne l'attendent pas gardent
    // exactement le comportement d'avant (aucune régression, aucune promesse
    // non tenue non plus).
    //
    // ═══════════════════════════════════════════════════════════════════════
    // 🔴 ET L'ÉCRASEMENT ENTRE ONGLETS — corrigé le 2026-09-08
    // ═══════════════════════════════════════════════════════════════════════
    // L'écriture passait par `storage.set` DEPUIS LE NAVIGATEUR : le blob
    // entier (607 Ko mesurés en base), dernier arrivé gagne, sans aucune
    // notion de version. Deux onglets ouverts — ou deux gestes rapprochés —
    // et le second écrasait le premier EN SILENCE.
    //
    // Dimitri : « deux onglets ouverts et je perds ma saisie de facturation
    // sans le voir — c'est de l'argent, et le remède existe déjà à côté ».
    //
    // Elle passe désormais par /api/data avec un jeton de version, exactement
    // comme `saveCfg` depuis le 2026-08-05. Sur conflit (409), le serveur
    // REFUSE et rien n'est écrit : le message est affiché tel quel et les
    // modifications restent à l'écran.
    try {
      // Bandeau (F13) : un geste enregistré retire « votre dernière
      // modification n'a pas été enregistrée » ; un import qui remet l'écran
      // sur la base pendant qu'une saisie refusée y restait le montre.
      await ecrireDonnees(d, { jeton: opts.jeton, adopterDonnees: setData, retirerBandeau: retirerBandeauData, montrerBandeau: montrerBandeauData })
      return true
    } catch (e) {
      console.error('saveData failed:', e)
      captureGarde('saveData', 'ÉCHEC PERSISTANCE fbz8-data', {
        error: e?.message || String(e),
        userRole: currentUser?.role || 'unknown',
      })
      if (e?.status === 409 && typeof opts.surConflit === 'function') {
        opts.surConflit()
        return false
      }
      // Le message du serveur est UTILE (conflit de version, garde
      // volumétrique) : il dit ce qui s'est passé et quoi faire. Le montrer
      // plutôt que de le laisser en console — c'est la leçon du 2026-08-05,
      // poussée d'un cran.
      setGardeAlert({
        type: 'ecriture',
        message: e?.message || "L'enregistrement a échoué. Rien n'a été enregistré.",
        userRole: currentUser?.role || 'unknown',
      })
      return false
    }
  }
  // Le bandeau de lecture porte aussi « Des changements ont eu lieu ailleurs ».
  const montrerBandeauData = (msg) => setLecturesIllisibles(prev => prev.includes(msg) ? prev : [...prev, msg])
  const retirerBandeauData = (msg) => setLecturesIllisibles(prev => prev.filter(m => m !== msg))
  return { saveCfg, saveCompta, saveData, montrerBandeauData, retirerBandeauData }
}
